import React, { useCallback, useMemo, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Box, Button } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { useParams } from 'react-router-dom';

import {
  useDeleteSandboxTrack,
  useDownloadSandboxTrack,
  useModerateSandbox,
  useRequestSandboxModerate,
  useSandboxTrackStatus,
  useStrategies,
  useToSandbox,
  getTrackErrorMessage,
} from './queries';
import { UploadTrackDialog } from './UploadTrackDialog';
import { ProtectedComponent } from '../../../components/ProtectedComponent';
import { TableGridLocal } from '../../../components/TableGridLocal';
import { RequestModerate, RequestModerateAction } from '../../../types/requestModerate';
import { StrategyStatus } from '../../../types/strategy';
import { UserAction } from '../../../utils/permissions';

export const Track: React.FC = () => {
  const { id: strategyId } = useParams<{ id: string }>();
  const [actionError, setActionError] = useState<string>('');
  const [isUploadTrackDialogOpen, setIsUploadTrackDialogOpen] = useState(false);

  const { data: strategies = [] } = useStrategies();
  const { data: trackStatus, error: trackStatusError } = useSandboxTrackStatus(strategyId);

  const error = trackStatus?.error || (trackStatusError ? getTrackErrorMessage(trackStatusError) : '');
  const uploading = trackStatus?.percentsDone || 0;
  const loadingTrack = Boolean(trackStatus?.loading);

  const { mutate: downloadSandboxTrack } = useDownloadSandboxTrack();
  const { mutate: sandboxDeleteTrack } = useDeleteSandboxTrack();
  const { mutate: sandboxRequestModerate } = useRequestSandboxModerate();
  const { mutate: sandboxModerate } = useModerateSandbox();
  const { mutate: toSandboxMutation } = useToSandbox();

  const strategy = useMemo(() => strategies.find((item) => item.id === strategyId), [strategyId, strategies]);

  const strategyStatus = useMemo(() => {
    if (uploading) {
      return `Загрузка ${uploading}%`;
    }

    if (strategy?.status === StrategyStatus.Moderate) {
      return 'На модерации';
    }

    if (strategy?.status === StrategyStatus.SandBox) {
      return 'Песочница';
    }

    if (strategy?.status === StrategyStatus.Closed) {
      return 'К Запуску';
    }

    if (strategy?.status === StrategyStatus.Standard) {
      return 'Запущена';
    }

    return '';
  }, [strategy?.status, uploading]);

  const openUploadTrackForm = useCallback(() => {
    setIsUploadTrackDialogOpen(true);
  }, []);

  const closeUploadTrackForm = useCallback(() => {
    setIsUploadTrackDialogOpen(false);
  }, []);

  const handleActionError = useCallback((requestError: unknown) => {
    setActionError(getTrackErrorMessage(requestError));
  }, []);

  const downloadTrack = useCallback(() => {
    setActionError('');
    downloadSandboxTrack(strategyId as string, {
      onError: handleActionError,
    });
  }, [downloadSandboxTrack, handleActionError, strategyId]);

  const deleteTrack = useCallback(() => {
    setActionError('');
    sandboxDeleteTrack(strategyId as string, {
      onError: handleActionError,
    });
  }, [handleActionError, sandboxDeleteTrack, strategyId]);

  const requestModerate = useCallback(
    (action: RequestModerateAction) => {
      const data: RequestModerate = {
        strategyId: strategyId as string,
        action,
      };
      setActionError('');
      sandboxRequestModerate(data, {
        onError: handleActionError,
      });
    },
    [handleActionError, sandboxRequestModerate, strategyId],
  );

  const moderate = useCallback(
    (action: RequestModerateAction) => {
      const data: RequestModerate = {
        strategyId: strategyId as string,
        action,
      };
      setActionError('');
      sandboxModerate(data, {
        onError: handleActionError,
      });
    },
    [handleActionError, sandboxModerate, strategyId],
  );

  const toSandbox = useCallback(() => {
    const data = {
      strategyId: strategyId as string,
    };
    setActionError('');
    toSandboxMutation(data, {
      onError: handleActionError,
    });
  }, [handleActionError, strategyId, toSandboxMutation]);

  const data = useMemo(
    () => [
      {
        status: strategyStatus,
        error: error,
        action: '',
      },
    ],
    [strategyStatus, error],
  );

  const columns = useMemo<
    MRT_ColumnDef<{
      status: string;
      error: string;
      action: string;
    }>[]
  >(
    () => [
      {
        header: 'Текущий статус',
        accessorKey: 'status',
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        header: 'Ошибки',
        accessorKey: 'error',
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        header: 'Доступные действия',
        accessorKey: 'action',
        size: 200,
        Cell: () => {
          return (
            <div>
              {strategy?.status === StrategyStatus.SandBox && (
                <>
                  <div>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      sx={{ mb: 2 }}
                      type="button"
                      onClick={downloadTrack}
                      startIcon={<DownloadIcon />}
                    >
                      Скачать трек
                    </Button>
                  </div>
                  <ProtectedComponent userAction={UserAction.sandboxTrackAdd}>
                    {!loadingTrack && (
                      <div>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ mb: 2 }}
                          type="button"
                          onClick={openUploadTrackForm}
                        >
                          Залить трек
                        </Button>
                      </div>
                    )}
                  </ProtectedComponent>
                  <ProtectedComponent userAction={UserAction.sandboxTrackRemove}>
                    <div>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ mb: 2 }}
                        type="button"
                        onClick={deleteTrack}
                      >
                        {loadingTrack ? 'Отменить загрузку' : 'Стереть трек'}
                      </Button>
                    </div>
                  </ProtectedComponent>
                  <ProtectedComponent userAction={UserAction.sandboxModerate}>
                    {!loadingTrack && (
                      <div>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          type="button"
                          onClick={() => requestModerate(RequestModerateAction.Yes)}
                        >
                          На модерацию
                        </Button>
                      </div>
                    )}
                  </ProtectedComponent>
                </>
              )}
              {strategy?.status === StrategyStatus.Moderate && (
                <>
                  <ProtectedComponent userAction={UserAction.sandboxModerateCancel}>
                    <div>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ mb: 2 }}
                        type="button"
                        onClick={() => moderate(RequestModerateAction.No)}
                      >
                        На доработку
                      </Button>
                    </div>
                  </ProtectedComponent>
                  <ProtectedComponent userAction={UserAction.sandboxModerateAccept}>
                    <div>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        sx={{ mb: 2 }}
                        type="button"
                        onClick={() => moderate(RequestModerateAction.Yes)}
                      >
                        Согласовать
                      </Button>
                    </div>
                  </ProtectedComponent>
                  <ProtectedComponent userAction={UserAction.sandboxModerate}>
                    <div>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        type="button"
                        onClick={() => requestModerate(RequestModerateAction.No)}
                      >
                        Отменить модерацию
                      </Button>
                    </div>
                  </ProtectedComponent>
                </>
              )}
              {(strategy?.status === StrategyStatus.Standard || strategy?.status === StrategyStatus.Closed) && (
                <ProtectedComponent userAction={UserAction.sandboxReturn}>
                  <div>
                    <Button variant="contained" color="primary" size="small" type="button" onClick={() => toSandbox()}>
                      В песочницу
                    </Button>
                  </div>
                </ProtectedComponent>
              )}
              {actionError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {actionError}
                </Alert>
              )}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
    ],
    [
      actionError,
      deleteTrack,
      downloadTrack,
      loadingTrack,
      moderate,
      openUploadTrackForm,
      requestModerate,
      strategy?.status,
      toSandbox,
    ],
  );

  return (
    <>
      <Box sx={{ mt: 4 }}>
        <TableGridLocal name="strategy-track" columns={columns} data={data} pageSize={25} totalRowCount={1} />
      </Box>
      {isUploadTrackDialogOpen && (
        <UploadTrackDialog strategyId={strategyId as string} onClose={closeUploadTrackForm} />
      )}
    </>
  );
};
